import { memo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Shared renderer for a tech-spec section's markdown — used by the section review card and
 * the edit-page outline. Mirrors SourceMarkdownPane's overrides so a docling ``` fence (e.g. a
 * big JSON API spec) WRAPS as readable text instead of a dark, horizontally-scrolling code box,
 * and spec tables render bordered/zebra. Deliberately NOT wrapped in Tailwind `prose`: prose
 * dark-boxes `<pre>` and injects backtick `::before` content that would fight these overrides.
 *
 * `REMARK_PLUGINS` + `COMPONENTS` are module-scope constants (stable identities), and the
 * component is `memo`'d — so an unrelated parent re-render (autosave status flipping, a sibling
 * card saving) doesn't re-run react-markdown over the whole (86 KB, in preview) document.
 */

const REMARK_PLUGINS = [remarkGfm];

const COMPONENTS: Components = {
  h1: ({ ...p }) => (
    <h1 {...p} className="text-[17px] font-bold tracking-tight text-navy mt-5 first:mt-0 mb-2" />
  ),
  h2: ({ ...p }) => (
    <h2 {...p} className="text-[15px] font-bold tracking-tight text-navy mt-5 first:mt-0 mb-2" />
  ),
  h3: ({ ...p }) => <h3 {...p} className="text-[14px] font-bold tracking-tight text-navy mt-4 first:mt-0 mb-1.5" />,
  h4: ({ ...p }) => <h4 {...p} className="text-[13px] font-semibold text-navy mt-3 first:mt-0 mb-1" />,
  p: ({ ...p }) => <p {...p} className="my-2" />,
  ul: ({ ...p }) => <ul {...p} className="list-disc pl-5 my-2 space-y-1" />,
  ol: ({ ...p }) => <ol {...p} className="list-decimal pl-5 my-2 space-y-1" />,
  li: ({ ...p }) => <li {...p} className="leading-[1.6] pl-0.5" />,
  strong: ({ ...p }) => <strong {...p} className="font-semibold text-navy" />,
  // Block code (a docling ``` fence — e.g. a big JSON API spec) renders PLAIN inside the
  // <pre> below and WRAPS so the whole thing shows; only genuinely-short inline code keeps
  // the little grey chip. Heuristic: a language class, a newline, or a long string = block.
  code: ({ className, children, ...p }) => {
    const text = String(children ?? '');
    const block = /language-/.test(className ?? '') || text.includes('\n') || text.length > 60;
    return block ? (
      <code {...p} className="font-mono text-[12px] whitespace-pre-wrap break-words">
        {children}
      </code>
    ) : (
      <code {...p} className="bg-[#eef0f3] py-0.5 px-1.5 rounded text-[12px] font-mono break-words">
        {children}
      </code>
    );
  },
  pre: ({ ...p }) => (
    <pre {...p} className="bg-[#fbfbfc] border border-line rounded-md p-3 my-2 text-[12px] font-mono leading-[1.5] whitespace-pre-wrap break-words" />
  ),
  // Tailwind preflight resets `a` to inherit colour and decoration, so a link in a spec read
  // as plain body text. New tab + noreferrer: spec links point off-site.
  a: ({ ...p }) => (
    <a
      {...p}
      target="_blank"
      rel="noreferrer noopener"
      className="text-electric underline underline-offset-2 [overflow-wrap:anywhere]"
    />
  ),
  table: ({ ...p }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-line">
      {/* First column emphasised (label column of a spec/key-value table). */}
      <table
        {...p}
        className="w-full border-collapse text-[13px] [&_td:first-child]:font-medium [&_td:first-child]:text-navy"
      />
    </div>
  ),
  th: ({ ...p }) => (
    <th
      {...p}
      className="text-left px-3 py-2 bg-line-soft text-muted font-semibold uppercase tracking-[0.04em] text-[10.5px] border-b border-line border-r border-line last:border-r-0 [overflow-wrap:anywhere]"
    />
  ),
  // Zebra striping on body rows (thead's single row stays unshaded) so a dense spec
  // table reads row-by-row, like the source sheet.
  tr: ({ ...p }) => <tr {...p} className="even:bg-line-soft/40" />,
  td: ({ ...p }) => (
    // `anywhere`, not `break-words`: only `anywhere` reduces the cell's min-content width, so
    // one long unbroken value (an ID, a hash, a pasted blob) wraps instead of stretching its
    // column far past the card and pushing every other column out of view behind a scrollbar.
    <td
      {...p}
      className="px-3 py-1.5 align-top border-b border-line-soft border-r border-line-soft last:border-r-0 [overflow-wrap:anywhere]"
    />
  ),
  blockquote: ({ ...p }) => (
    <blockquote {...p} className="border-l-4 border-electric pl-4 my-2 text-muted italic" />
  ),
  hr: ({ ...p }) => <hr {...p} className="my-3 border-line" />,
};

export const SpecMarkdown = memo(function SpecMarkdown({ markdown }: { markdown: string }) {
  return (
    <div className="text-[13.5px] leading-[1.7] text-ink break-words">
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
});
